import { ApiService } from '@/service/api'
import type {
  TraceFileResponse,
  TraceResponse,
  TraceStepFull,
} from './types'

/**
 * Feature-local API client for the /v1/kg-trace/* surface (AGNT-685).
 * Routes through the global ApiService.fetchOrError so auth +
 * correlation-id + Fabric-embed handling stay centralized.
 *
 * Each method returns null on 404 so callers can render empty / not-
 * found states without try/catch ceremony. Other errors throw to the
 * caller's error boundary.
 */
export class KgTraceApi {
  constructor(private readonly api: ApiService) {}

  async getTrace(correlationId: string): Promise<TraceResponse | null> {
    return this.fetchJsonOrNull<TraceResponse>(
      this.api.getUrl(`v1/kg-trace/${encodeURIComponent(correlationId)}`),
    )
  }

  async getFile(correlationId: string, path: string): Promise<TraceFileResponse | null> {
    return this.fetchJsonOrNull<TraceFileResponse>(
      this.api.getUrl(`v1/kg-trace/${encodeURIComponent(correlationId)}/file`, { path }),
    )
  }

  /** The full, untruncated input/output for one step (AGNT-1064) — backs the
   *  StepInspector "Load full payload" button when a step is truncated. */
  async getStepFull(correlationId: string, stepId: string): Promise<TraceStepFull | null> {
    return this.fetchJsonOrNull<TraceStepFull>(
      this.api.getUrl(
        `v1/kg-trace/${encodeURIComponent(correlationId)}/step/${encodeURIComponent(stepId)}/full`,
      ),
    )
  }

  private async fetchJsonOrNull<T>(url: string): Promise<T | null> {
    try {
      const res = await this.api.fetchOrError(url)
      return (await res.json()) as T
    } catch (err) {
      // ApiService.fetchOrError throws an Error with `response.status`
      // attached on non-2xx. Distinguish 404 → null (clean "not found"
      // UX) from other errors which should propagate to the page's
      // error boundary. Avoid string-matching on `err.message` so a
      // backend message that happens to contain "404" doesn't cause
      // a silent miss.
      const status = (err as { response?: { status?: number } })?.response?.status
      if (status === 404) return null
      throw err
    }
  }
}
